export class HttpError extends Error {
  status: number;
  body?: unknown;
  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export interface HttpResponse<T> {
  status: number;
  data: T;
}

/**
 * Same request/error semantics as `http`, but the HTTP status survives.
 *
 * Needed by endpoints where a success status is load-bearing rather than
 * decorative — the telemetry lake answers 202 with a query id when Athena
 * misses the inline deadline, and a client that only sees the body cannot tell
 * that apart from a finished answer.
 */
export async function httpWithStatus<T>(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<HttpResponse<T>> {
  const res = await fetch(input, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.headers || {}),
    },
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const body = isJson ? await res.json().catch(() => undefined) : await res.text().catch(() => undefined);

  if (!res.ok) {
    const msg = (typeof body === "object" && body && "error" in (body as any)) ? String((body as any).error) : res.statusText;
    throw new HttpError(msg || `HTTP ${res.status}`, res.status, body);
  }

  return { status: res.status, data: body as T };
}

export async function http<T>(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<T> {
  const { data } = await httpWithStatus<T>(input, init);
  return data;
}
